import { IconButton, mergeStyles, MessageBar, MessageBarType, Stack, Text, useTheme } from '@fluentui/react';
import { useAuthentication } from 'global/Authentication';
import { EModelContext } from 'global/Compare/ComparisonEngine';
import { CascadeChildMergeActions, determineOptimalMergeAction, EMergeVMStatus, IEntitiesContainerVM, IGroupConfigurationComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import { IComment, ICommentRouteRef } from 'model/FlexibleAttributeModel';
import React, { CSSProperties, useMemo } from 'react';
import { ECompareConfigurationExpandOptions, ICompareConfiguration } from '../CompareLookupDialog/CompareLookupDialog';
import CompareTable from '../Pharmacy/PharmacyPlan/PharmacyCompareTable';
import { TABLE_STYLES } from '../Pharmacy/PharmacyPlan/PharmacyPlan.constants';
import CommentReviewPanel, { ECommentReviewPanelViewType, navigateToLocation } from './CommentReviewPanel/CommentReviewPanel';
import { IMarkerSet } from './Compare.constants';
import { TSetCommentInfoDelegate } from './CompareContext';
import CompareQuickNav from './CompareQuickNavOverlay';
import CompareRow from './CompareRow';
import { CompareTitle } from './CompareTitle';
import CustomPanel, { EPanelStyleType } from './CustomPanel';
import EntityComparePanel from './EntityComparePanel';
import { MergeActionMenu } from './MergeActionMenu';

const STICKY_HEADER_HEIGHT = 36;
const ORIGIN_TITLE = 'Origin';
const TARGET_TITLE = 'Target';
const QUICK_NAV_MIN_WIDTH = 320;
const COMMENTS_PANEL_MIN_WIDTH = 320;
const EMBEDDED_EVENT = 'compare-quick-nav-embedded-change';

const getDefaultCollapsed = (configuration: ICompareConfiguration, changed?: boolean) => {

    if (configuration.expandMode === ECompareConfigurationExpandOptions.all) {
        return false;
    }

    if (configuration.expandMode === ECompareConfigurationExpandOptions.differences) {
        return !changed;
    }

    return true;
};

interface ICompareListSectionProps {
    model: IGroupConfigurationComparisonVM;
    configuration: ICompareConfiguration;
    markers: IMarkerSet;
    onSetCommentInfo: TSetCommentInfoDelegate;
    isMergeApp?: boolean;
    isUnderwritingReviewApp?: boolean;
    mergeActions?: Record<string, EMergeVMStatus>;
    comments: Record<string, IComment>;
    isCommentReviewOpen?: boolean;
    onDismissCommentReview?: () => void;
    onToggleCommentResolved?: (comment: IComment) => void;
    onEditComment?: (comment: IComment) => void;
    isQuickNavOpen: boolean;
    onDismissQuickNav?: () => void;
}

const CompareListSection: React.FC<ICompareListSectionProps> = (props) => {

    const [entityRenderVersion, setEntityRenderVersion] = React.useState<Record<string, number>>({});

    // Quick navigation panel refs and state
    const [isQuickNavPinned, setIsQuickNavPinned] = React.useState(false);
    const quickNavContainerRef = React.useRef<HTMLDivElement | null>(null);
    const quickNavWidthRef = React.useRef(420);
    const quickNavResizeCleanupRef = React.useRef<(() => void) | null>(null);

    // Comments panel refs and state
    const [isCommentsPinned, setIsCommentsPinned] = React.useState(false);
    const commentsContainerRef = React.useRef<HTMLDivElement | null>(null);
    const commentsWidthRef = React.useRef(340);
    const commentsResizeCleanupRef = React.useRef<(() => void) | null>(null);

    const authentication = useAuthentication();

    const getEntityRenderKey = React.useCallback((panelId: string, entityId: string) => `${panelId}:${entityId}`, []);

    const remountEntityPanels = React.useCallback((keys: string[]) => {
        if (keys.length === 0)
            return;

        setEntityRenderVersion(prev => {
            const next = { ...prev };

            keys.forEach(key => {
                next[key] = (next[key] ?? 0) + 1;
            });

            return next;
        });
    }, []);

    const applyQuickNavWidth = React.useCallback((width: number) => {
        quickNavWidthRef.current = width;

        const element = quickNavContainerRef.current;
        if (!element)
            return;

        const value = `${width}px`;
        element.style.width = value;
        element.style.minWidth = value;
    }, []);

    const startResizeQuickNav = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();

        const element = quickNavContainerRef.current;
        if (!element)
            return;

        const startX = event.clientX;
        const startWidth = element.getBoundingClientRect().width || quickNavWidthRef.current;
        const maxWidth = Math.max(QUICK_NAV_MIN_WIDTH, Math.floor(window.innerWidth * 0.7));

        element.style.transition = 'none';
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaX = moveEvent.clientX - startX;
            const nextWidth = Math.min(maxWidth, Math.max(QUICK_NAV_MIN_WIDTH, startWidth + deltaX));
            applyQuickNavWidth(nextWidth);
        };

        const handleMouseUp = () => {
            if (quickNavContainerRef.current) {
                quickNavContainerRef.current.style.transition = 'width 0.2s ease';
            }

            document.body.style.userSelect = '';
            document.body.style.cursor = '';

            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);

            if (quickNavResizeCleanupRef.current) {
                quickNavResizeCleanupRef.current = null;
            }
        };

        quickNavResizeCleanupRef.current = handleMouseUp;
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    }, [applyQuickNavWidth]);

    const applyCommentsWidth = React.useCallback((width: number) => {
        commentsWidthRef.current = width;

        const element = commentsContainerRef.current;
        if (!element)
            return;

        const value = `${width}px`;
        element.style.width = value;
        element.style.minWidth = value;
    }, []);

    const startResizeComments = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();

        const element = commentsContainerRef.current;
        if (!element)
            return;

        const startX = event.clientX;
        const startWidth = element.getBoundingClientRect().width || commentsWidthRef.current;
        const maxWidth = Math.max(COMMENTS_PANEL_MIN_WIDTH, Math.floor(window.innerWidth * 0.7));

        element.style.transition = 'none';
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaX = startX - moveEvent.clientX;
            const nextWidth = Math.min(maxWidth, Math.max(COMMENTS_PANEL_MIN_WIDTH, startWidth + deltaX));
            applyCommentsWidth(nextWidth);
        };

        const handleMouseUp = () => {
            if (commentsContainerRef.current) {
                commentsContainerRef.current.style.transition = 'width 0.2s ease';
            }

            document.body.style.userSelect = '';
            document.body.style.cursor = '';

            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);

            if (commentsResizeCleanupRef.current) {
                commentsResizeCleanupRef.current = null;
            }
        };

        commentsResizeCleanupRef.current = handleMouseUp;
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    }, [applyCommentsWidth]);

    React.useEffect(() => {
        return () => {
            quickNavResizeCleanupRef.current?.();
            commentsResizeCleanupRef.current?.();
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
    }, []);

    React.useEffect(() => {
        window.dispatchEvent(
            new CustomEvent(EMBEDDED_EVENT, {
                detail: {
                    embedded: isQuickNavPinned || isCommentsPinned,
                },
            })
        );

        return () => {
            window.dispatchEvent(
                new CustomEvent(EMBEDDED_EVENT, {
                    detail: {
                        embedded: false,
                    },
                })
            );
        };
    }, [isQuickNavPinned, isCommentsPinned]);

    const onNavigateComment = React.useCallback((rootLocation: ICommentRouteRef) => {
        void navigateToLocation(rootLocation);
    }, []);

    const theme = useTheme();

    const classNames = useMemo(() => {
        return mergeStyles({
            "td": {},
            "tr": {
                "& .highlight": {
                    ":nth-child(even)": {
                        backgroundColor: theme.isInverted ? 'rgb(67, 53, 25)' : 'rgb(240, 230, 200)',
                    },
                    ":nth-child(odd)": {
                        backgroundColor: theme.isInverted ? 'rgb(67, 65, 35)' : 'rgb(255, 250, 221)',
                    },
                },
                ":hover": {
                    backgroundColor: theme.semanticColors.warningBackground,
                },
                "& .highlight:hover": {
                    backgroundColor: theme.semanticColors.warningBackground,
                },
            },
            "& > :nth-child(even)": {
                backgroundColor: theme.semanticColors.bodyDivider,
            },
        });
    }, [theme]);


    const renderSummary = () => {
        if (!props.model || !props.model.summary)
            return null;

        if (props.configuration.showDifferencesOnly && !props.model.summary.changed)
            return null;

        const children: JSX.Element[] = [];

        props.model.summary.rowsVM.forEach((property) => {
            if (props.configuration.showDifferencesOnly && !property.changed)
                return;

            children.push(
                <CompareRow
                    key={property.propertyDefinition.name}
                    name={<CompareTitle
                        changed={property.changed}
                        name={property.propertyDefinition.name}
                        entityProps={{
                            markers: props.markers,
                        }}
                    />}
                    valueVM={{
                        origin: property.origin,
                        target: property.target,
                        changed: property.changed,
                        mergeAction: property.mergeAction,
                    }}
                    entityProps={{
                        isUnderwritingApp: props.isUnderwritingReviewApp,
                        isMergeApp: props.isMergeApp,
                    }}
                />
            );
        });

        return (
            <CustomPanel
                id={'summary-panel'}
                title={<CompareTitle
                    changed={props.model.summary.changed}
                    name={'Configuration Summary'}
                    entityProps={{
                        markers: props.markers,
                    }}
                />}
                canCollapse={true}
                defaultCollapsed={getDefaultCollapsed(props.configuration, props.model.summary.changed)}
                stickyHeader={true}
                stickyTop={0}
                stickyZIndex={100}
                styleType={props.isMergeApp
                    ? EPanelStyleType.Gray
                    : EPanelStyleType.Default
                }
            >
                <CompareTable
                    className={classNames}
                    tableStyles={TABLE_STYLES}
                    originHeader={ORIGIN_TITLE}
                    targetHeader={TARGET_TITLE}
                    hideActionHeader={true}
                >
                    {children}
                </CompareTable>
            </CustomPanel>
        );
    };

    const renderComparison = (
        category: IEntitiesContainerVM,
    ) => {

        if (!category)
            return null;

        if (props.configuration.showDifferencesOnly && !category.changed)
            return null;

        let hideMergeAction = false;

        // In the group configuration context, we don't want to show the merge action menu,
        // since it doesn't make sense to merge the group configuration itself at root level
        if (category.context === EModelContext.groupConfiguration) {
            hideMergeAction = true;
        }

        const onPreferMergeAction = (mergeAction: EMergeVMStatus) => {
            const keysToRefresh: string[] = [];

            category.entities.forEach(entityVM => {

                if (entityVM.changed) {

                    const optimalMergeAction = determineOptimalMergeAction(
                        entityVM.compareResult,
                        mergeAction,
                        true,
                        category.context
                    );

                    CascadeChildMergeActions(
                        optimalMergeAction,
                        null,
                        entityVM,
                        null,
                        null,
                    );

                    keysToRefresh.push(getEntityRenderKey(category.context, entityVM.id));
                }
            });

            remountEntityPanels(keysToRefresh);
        }


        return (
            <CustomPanel
                id={category.context}
                data-category={category.context}
                title={<CompareTitle
                    changed={category.changed}
                    name={category.title}
                    entityProps={{
                        markers: props.markers,
                    }}
                />}
                headerRight={props.isMergeApp && !hideMergeAction && (
                    <MergeActionMenu
                        list={category}
                        onPrefer={onPreferMergeAction}
                    />
                )}
                canCollapse={true}
                defaultCollapsed={getDefaultCollapsed(props.configuration, category.changed)}
                stickyHeader={true}
                stickyTop={0}
                stickyZIndex={90}
                styleType={EPanelStyleType.Default}
            >
                <Stack
                    tokens={{ childrenGap: '8px' }}
                    style={{ marginBottom: '5px', marginTop: '5px' }}
                >
                    {category.entities.map((eachEntity) => {
                        if (props.configuration.showDifferencesOnly && !eachEntity.changed)
                            return null;

                        return (
                            <EntityComparePanel
                                key={`${eachEntity.id}-${entityRenderVersion[getEntityRenderKey(category.context, eachEntity.id)] ?? 0}`}
                                compareVM={eachEntity}
                                stickyOffset={STICKY_HEADER_HEIGHT}
                                configuration={props.configuration}
                                classNames={classNames}
                                markers={props.markers}
                                onSetCommentInfo={props.onSetCommentInfo}
                                isMergeApp={props.isMergeApp}
                                isUnderwritingApp={props.isUnderwritingReviewApp}
                                mergeDisabled={eachEntity.mergeAction === EMergeVMStatus.Disabled}
                                comments={props.comments}
                                authentication={authentication}
                            />
                        );
                    })}
                </Stack>
            </CustomPanel>
        );
    };

    const renderMessages = () => {
        if (!props.model)
            return null;

        if (props.isMergeApp) {
            const isPendingMode = props.configuration.defaultMergeAction === EMergeVMStatus.Pend;

            return (
                <MessageBar
                    messageBarType={MessageBarType.info}
                >
                    <Stack tokens={{ childrenGap: '8px' }}>
                        <Text>
                            Changes are applied to the Target when the merge completes.
                        </Text>
                        <Text>
                            Unchanged entities will remain as is in the Target.
                        </Text>
                        {isPendingMode && (
                            <Text>
                                Pending mode is active. No merge actions are preselected or cascaded; select each required merge action before previewing or saving.
                            </Text>
                        )}
                        {!isPendingMode && (
                            <Text>
                                Merge selections are cascaded to child entities. Review each individual item to configure additional merge options.
                            </Text>
                        )}
                    </Stack>
                </MessageBar>
            );
        }
    }



    const compareListMemo = useMemo(() => {

        const { model } = props;

        if (!model)
            return null;

        return (
            <Stack horizontal tokens={{ childrenGap: '8px' }} styles={{ root: { alignItems: 'flex-start' } }}>
                {renderQuickNav()}
                {renderCategoryX(model)}
                {renderComments()}
            </Stack>
        );

        function renderCategoryX(model: IGroupConfigurationComparisonVM) {

            return (
                <Stack grow tokens={{ childrenGap: '8px' }}>
                    {renderMessages()}
                    {renderSummary()}
                    {renderComparison(model.groupConfiguration)}
                    {renderComparison(model.nonStandardTemplate)}
                    {renderComparison(model.plan)}
                    {renderComparison(model.benefit)}
                    {renderComparison(model.accountsReceivable)}
                    {renderComparison(model.groupNumber)}
                    {renderComparison(model.cesNumber)}
                    {renderComparison(model.department)}
                    {renderComparison(model.feesAndPremium)}
                    {!props.isMergeApp &&
                        renderComparison(model.feesSummary)}
                    {!props.isMergeApp &&
                        renderComparison(model.stopLossSummary)}
                    {renderComparison(model.stopLoss)}
                </Stack>
            );
        }

        function renderComments() {
            if (!isCommentsPinned && !props.isCommentReviewOpen)
                return null;

            const style: CSSProperties = {
                position: 'sticky',
                top: 0,
                width: `${commentsWidthRef.current}px`,
                minWidth: `${commentsWidthRef.current}px`,
                alignSelf: 'flex-start',
                transition: 'width 0.2s ease',
                zIndex: 110,
                overflow: 'visible',
            }

            const commentReviewPanel = (
                <CommentReviewPanel
                    comments={Object.values(props.comments)}
                    viewType={isCommentsPinned ? ECommentReviewPanelViewType.Embedded : ECommentReviewPanelViewType.SidePanel}
                    isOpen={!!props.isCommentReviewOpen}
                    dismissPanel={props.onDismissCommentReview}
                    onToggleResolved={props.onToggleCommentResolved}
                    onEditComment={props.onEditComment}
                    onNavigate={(rootLocation) => {
                        setTimeout(() => {
                            onNavigateComment(rootLocation);
                        }, 350);

                        if (!isCommentsPinned)
                            props.onDismissCommentReview?.();
                    }}
                    setPinned={() => setIsCommentsPinned(!isCommentsPinned)}
                />
            );

            return <div
                ref={commentsContainerRef}
                style={isCommentsPinned ? style : undefined}
            >
                {isCommentsPinned ? (
                    <div style={{ position: 'relative', height: '100%' }}>
                        {commentReviewPanel}
                        <div
                            role='separator'
                            aria-orientation='vertical'
                            aria-label='Resize comments panel'
                            onMouseDown={startResizeComments}
                            style={{
                                position: 'absolute',
                                marginLeft: '4px',
                                marginRight: '4px',
                                top: 0,
                                left: -6,
                                height: '100%',
                                width: 8,
                                cursor: 'col-resize',
                                zIndex: 120,
                            }}
                        />
                    </div>
                ) : (
                    commentReviewPanel
                )}
            </div>;
        }

        function renderQuickNav() {
            if (!isQuickNavPinned && !props.isQuickNavOpen)
                return null;

            const quickNavOverlay = (
                <CompareQuickNav
                    model={model}
                    configuration={props.configuration}
                    markers={props.markers}
                    isHidden={!props.isQuickNavOpen}
                    embedded={isQuickNavPinned}
                    onTogglePinned={() => setIsQuickNavPinned((v) => !v)}
                    onDismiss={props.onDismissQuickNav}
                />
            );

            return <div
                ref={quickNavContainerRef}
                style={{
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                    width: isQuickNavPinned ? `${quickNavWidthRef.current}px` : 0,
                    minWidth: isQuickNavPinned ? `${quickNavWidthRef.current}px` : 0,
                    transition: 'width 0.2s ease',
                    zIndex: 110,
                    overflow: 'visible',
                }}
            >
                {isQuickNavPinned ? (
                    <div style={{ position: 'relative', height: '100%' }}>
                        {quickNavOverlay}
                        <div
                            role='separator'
                            aria-orientation='vertical'
                            aria-label='Resize quick navigation panel'
                            onMouseDown={startResizeQuickNav}
                            style={{
                                position: 'absolute',
                                top: 0,
                                right: -4,
                                height: '100%',
                                width: 8,
                                cursor: 'col-resize',
                                zIndex: 120,
                                borderRight: `2px solid ${theme.semanticColors.bodyDivider}`,
                            }}
                        />
                    </div>
                ) : (
                    quickNavOverlay
                )}
            </div>;
        }
    }, [
        props.model,
        props.configuration,
        props.markers,
        props.isMergeApp,
        props.isUnderwritingReviewApp,
        props.mergeActions,
        props.comments,
        props.isCommentReviewOpen,
        props.onDismissCommentReview,
        props.onToggleCommentResolved,
        props.onEditComment,
        onNavigateComment,
        getEntityRenderKey,
        remountEntityPanels,
        startResizeQuickNav,
        startResizeComments,
        theme,
        classNames,
        entityRenderVersion,
        isQuickNavPinned,
        isCommentsPinned,
        setIsCommentsPinned,
    ]);

    return compareListMemo;
};

export default CompareListSection;



